/**
 * Business Controls (v108): one control center for pricing, booking rules, complimentary services and coupons. Trera,
 * 22 Sep 2026: "it basically consolidates a few of the live pages together." So it does: the Booking Rules tab is the
 * existing Booking Attempts page (settings + blocked attempts), Promotions & Coupons is the existing Promotions page,
 * add-on prices are the existing Add-On Pricing page. Traci, 23 Sep 2026: "we want to be able to edit this at all
 * times": the package ladder and the late-booking rule are editable by Admin into the price book
 * (src/lib/pricing/priceBook.js, audited); checkout reads them once the fold-in ships. Complimentary Services is a
 * form over the existing admin add-on route in its $0 mode. Notifications (27 Sep 2026): the client and talent email
 * and text templates, on/off, preview and test send (./NotificationTemplates.tsx).
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/components/admin/ui/button';
import type { ReactNode } from 'react';
import { useSearchParamState, enumCodec } from '@/hooks/useSearchParamState';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/admin/ui/card';
import { MAX_PHOTOS, PHOTO_STEP, PHOTO_STEP_PRICE } from '@/lib/constants/packagePricing';
import { PACKAGE_TYPES, HOURS } from '@/lib/pricing/priceBook';
import { stagingFetch } from '../api';
import { useStaging } from '../StagingLayout';
import BookingAttempts from '@/pages/admin/BookingAttempts';
import { supabaseAdmin } from '@/integrations/supabase/client';
import Promotions from '@/pages/admin/Promotions';
import AddOnPricing from '@/pages/admin/AddOnPricing';
import ComplimentaryServices from './ComplimentaryServices';
import NotificationTemplates from './NotificationTemplates';

const TABS = [
  { key: 'pricing', label: 'Pricing' },
  { key: 'rules', label: 'Booking Rules' },
  { key: 'complimentary', label: 'Complimentary Services' },
  { key: 'coupons', label: 'Promotions & Coupons' },
  { key: 'notifications', label: 'Notifications' }
];
const tabCodec = enumCodec(TABS.map((t) => t.key), 'pricing');
const money = (n: number | null | undefined) => (n === null || n === undefined ? '—' : '$' + Number(n).toLocaleString('en-US'));

/** Shared by the Pricing and Booking Rules sections: the price book as the server resolves it. */
function usePriceBook() {
  const [data, setData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const load = () => stagingFetch('/api/admin/staging/pricing').then((d) => { setData(d); setError(null); }).catch((e) => setError(e.message || String(e)));
  useEffect(() => { load(); }, []);
  return { data, error, reload: load };
}

function FoldInNote({ data }: { data: any }) {
  if (!data || data.folded_in) return null;
  return (
    <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
      Saved package prices are recorded here with who changed them and when. Checkout and the homepage switch to reading
      them when the pricing fold-in ships; until then the site keeps the built-in ladder. Booking Rules (minimum notice,
      short-notice bookings, the late booking window and fee) and add-on prices (below) are live: checkout reads them
      within a minute of saving.
    </p>
  );
}

function PricingTab() {
  const { role } = useStaging();
  const { data, error, reload } = usePriceBook();
  const [edits, setEdits] = useState<Record<string, Record<number, string>>>({});
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const canEdit = Boolean(data?.can_edit) && role === 'admin';
  const dirty = Object.values(edits).some((m) => Object.keys(m).length > 0);
  const save = async () => {
    setSaving(true);
    try {
      const r = await stagingFetch('/api/admin/staging/pricing', { method: 'PUT', body: JSON.stringify({ packages: edits, note }) });
      toast.success(r.unchanged ? 'Nothing changed' : `Saved: ${r.changes.join(' · ')}`);
      setEdits({}); setNote(''); await reload();
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setSaving(false); }
  };
  if (error) return <p className="text-sm text-destructive">Could not load pricing: {error}</p>;
  if (!data) return <p className="text-sm text-muted-foreground">Loading pricing…</p>;
  return (
    <div className="space-y-6">
      <FoldInNote data={data} />
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Wedding Packages <span className="text-xs font-normal text-muted-foreground ml-2">all 1–12 coverage hours · baseline package pricing</span></CardTitle>
          <p className="text-xs text-muted-foreground">The minimum price for each coverage length. Photo-count tiers above the base ({PHOTO_STEP} photos for ${PHOTO_STEP_PRICE}, up to the playbook&apos;s cap per size) stay in the package flow.{canEdit ? ' Edit a cell, add a note, save.' : ''}</p></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase text-muted-foreground"><th className="py-1">Coverage</th>{PACKAGE_TYPES.map((t) => <th key={t.key} className="text-right">{t.label}</th>)}<th className="text-right">Photo cap</th></tr></thead>
            <tbody>{HOURS.map((h) => (
              <tr key={h} className="border-t">
                <td className="py-1.5 font-medium">{h} HR</td>
                {PACKAGE_TYPES.map((t) => {
                  const cur = data.packages.prices[t.key][h];
                  const val = edits[t.key]?.[h];
                  return (
                    <td key={t.key} className="text-right py-1">
                      {canEdit ? (
                        <span className="inline-flex items-center gap-1"><span className="text-muted-foreground">$</span>
                          <input aria-label={`${t.label} ${h} hours`} inputMode="numeric" className={`w-24 rounded-md border px-2 py-1 text-right tabular-nums ${val !== undefined ? 'border-primary bg-accent/40' : 'bg-background'} ${data.packages.source[t.key][h] === 'price_book' ? 'font-semibold' : ''}`}
                            value={val ?? String(cur ?? '')} onChange={(e) => setEdits((x) => { const n = { ...x, [t.key]: { ...(x[t.key] || {}) } }; const v = e.target.value.replace(/[^0-9]/g, ''); if (v === String(cur)) delete n[t.key][h]; else n[t.key][h] = v; return n; })} />
                        </span>
                      ) : <span className="tabular-nums">{money(cur)}</span>}
                    </td>
                  );
                })}
                <td className="text-right tabular-nums text-muted-foreground">{MAX_PHOTOS.photo[h as keyof typeof MAX_PHOTOS.photo]} photos</td>
              </tr>
            ))}</tbody>
          </table>
          {canEdit && (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <input className="h-9 flex-1 min-w-[220px] rounded-md border bg-background px-2 text-sm" placeholder="Note for the audit log (optional)" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Pricing note" />
              <Button variant="outline" disabled={!dirty || saving} onClick={() => setEdits({})}>Discard</Button>
              <Button disabled={!dirty || saving} onClick={save}>Save Pricing Changes</Button>
            </div>
          )}
          <p className="text-[11px] text-muted-foreground mt-2">Bold cells come from the price book; the rest are the built-in ladder. Every change records old value, new value, who and when.</p>
          {data.history?.length > 0 && (
            <details className="mt-2 text-xs"><summary className="cursor-pointer text-muted-foreground">Recent price changes ({data.history.length})</summary>
              <ul className="mt-1 space-y-1">{data.history.map((h: any) => <li key={h.id}><span className="text-muted-foreground">{new Date(h.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/Denver' })} · {h.performed_by_name || 'Staff'}:</span> {h.details}</li>)}</ul>
            </details>
          )}
        </CardContent>
      </Card>

      {/* Traci's doc, Oct 2026: the Video Add-On price card is removed; its prices are the Video Add-On row in Add-On Pricing below */}
      <div className="-mx-8 -mb-8"><AddOnPricing /></div>
    </div>
  );
}

function RulesTab() {
  const { role } = useStaging();
  const { data, error, reload } = usePriceBook();
  const [windowDays, setWindowDays] = useState('');
  const [fee, setFee] = useState('');
  const [minDays, setMinDays] = useState('');
  const [shortNotice, setShortNotice] = useState(false);
  const [loaded, setLoaded] = useState<{ min: string; short: boolean } | null>(null);
  const [saving, setSaving] = useState(false);
  const canEdit = Boolean(data?.can_edit) && role === 'admin';
  useEffect(() => { if (data) { setWindowDays(String(data.late.window_days)); setFee(String(data.late.fee_per_member_cents / 100)); } }, [data]);
  // Minimum notice and the short-notice switch: the same system_settings rows the current Booking Attempts page edits
  const loadRules = async () => {
    const { data: rows } = await supabaseAdmin.from('system_settings').select('setting_key, setting_value').in('setting_key', ['minimum_booking_days', 'allow_short_notice_bookings']);
    const min = String(parseInt(rows?.find((r: any) => r.setting_key === 'minimum_booking_days')?.setting_value || '10', 10) || 10);
    const short = rows?.find((r: any) => r.setting_key === 'allow_short_notice_bookings')?.setting_value === 'true';
    setMinDays(min); setShortNotice(short); setLoaded({ min, short });
  };
  useEffect(() => { loadRules(); }, []);
  const lateDirty = Boolean(data) && (windowDays !== String(data.late.window_days) || fee !== String(data.late.fee_per_member_cents / 100));
  const rulesDirty = Boolean(loaded) && (minDays !== loaded!.min || shortNotice !== loaded!.short);
  const save = async () => {
    setSaving(true);
    try {
      const done: string[] = [];
      if (rulesDirty) {
        const a = await supabaseAdmin.from('system_settings').upsert({ setting_key: 'minimum_booking_days', setting_value: String(Number(minDays) || 0), setting_type: 'number', description: 'Minimum number of days required before the event date', category: 'booking' }, { onConflict: 'setting_key' });
        if (a.error) throw a.error;
        const b = await supabaseAdmin.from('system_settings').upsert({ setting_key: 'allow_short_notice_bookings', setting_value: String(shortNotice), setting_type: 'boolean', description: 'Allow customers to book events with less than minimum days notice', category: 'booking' }, { onConflict: 'setting_key' });
        if (b.error) throw b.error;
        done.push(`minimum notice ${minDays} days`, `short-notice ${shortNotice ? 'on' : 'off'}`);
        await loadRules();
      }
      if (lateDirty) {
        const r = await stagingFetch('/api/admin/staging/pricing', { method: 'PUT', body: JSON.stringify({ late: { window_days: Number(windowDays), fee_per_member_cents: Math.round(Number(fee) * 100) } }) });
        if (!r.unchanged) done.push(...r.changes);
        await reload();
      }
      toast.success(done.length ? `Saved: ${done.join(' · ')}` : 'Nothing changed');
    } catch (e: any) { toast.error(e.message || String(e)); } finally { setSaving(false); }
  };
  const card = 'rounded-lg border bg-card p-4 space-y-2';
  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-destructive">Could not load the rules: {error}</p>}
      <FoldInNote data={data} />
      <div className="grid gap-3 lg:grid-cols-3">
        <div className={card}>
          <b className="text-sm">Minimum Booking Notice</b>
          <p className="text-xs text-muted-foreground">The fewest days before the event a client may book while short-notice bookings are on. Off, the standard 10 days applies.</p>
          <span className="flex items-center gap-2 text-sm"><input aria-label="Minimum booking notice days" disabled={!canEdit} inputMode="numeric" className="w-20 rounded-md border bg-background px-2 py-1 text-right tabular-nums" value={minDays} onChange={(e) => setMinDays(e.target.value.replace(/[^0-9]/g, ''))} /> days</span>
        </div>
        <div className={card}>
          <div className="flex items-center justify-between gap-2"><b className="text-sm">Allow Short-Notice Bookings</b>
            <button type="button" role="switch" aria-checked={shortNotice} aria-label="Allow short-notice bookings" disabled={!canEdit} onClick={() => setShortNotice(!shortNotice)} className={`relative h-5 w-9 rounded-full border transition ${shortNotice ? 'bg-primary border-primary' : 'bg-muted'}`}><span className={`absolute top-0.5 h-3.5 w-3.5 rounded-full bg-white shadow transition ${shortNotice ? 'left-4' : 'left-0.5'}`} /></button>
          </div>
          <p className="text-xs text-muted-foreground">On: clients may book down to the Minimum Booking Notice. Off: bookings need the standard 10 days notice.</p>
          <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-semibold ${shortNotice ? 'border-emerald-300 bg-emerald-50 text-emerald-800' : 'border-slate-300 text-slate-700'}`}>{shortNotice ? 'Enabled' : 'Disabled'}</span>
        </div>
        <div className={card}>
          <b className="text-sm">Late Booking Fee Rule</b>
          <p className="text-xs text-muted-foreground">Applied when a wedding is booked {windowDays || '…'} day{windowDays === '1' ? '' : 's'} or less before the event, per team member staffed (photo + video = two).</p>
          <div className="flex flex-wrap items-end gap-3">
            <label className="text-xs">Late Booking Window<span className="mt-1 flex items-center gap-2 text-sm"><input aria-label="Late booking window days" disabled={!canEdit} inputMode="numeric" className="w-16 rounded-md border bg-background px-2 py-1 text-right tabular-nums" value={windowDays} onChange={(e) => setWindowDays(e.target.value.replace(/[^0-9]/g, ''))} /> days or less</span></label>
            <label className="text-xs">Late Booking Fee<span className="mt-1 flex items-center gap-2 text-sm">$<input aria-label="Late booking fee per team member" disabled={!canEdit} inputMode="decimal" className="w-20 rounded-md border bg-background px-2 py-1 text-right tabular-nums" value={fee} onChange={(e) => setFee(e.target.value.replace(/[^0-9.]/g, ''))} /> per team member</span></label>
          </div>
        </div>
      </div>
      {canEdit && <div className="flex justify-end"><Button disabled={(!lateDirty && !rulesDirty) || saving} onClick={save}>Save Booking Rules</Button></div>}
      <BookingAttempts embedded />
    </div>
  );
}

export default function BusinessControls() {
  // One long page, as in Traci's prototype: the four buttons scroll to their section (Trera, 22 Sep 2026: "the buttons
  // just bring you to the position on the page"). The section is kept in the URL so a link lands on it.
  const [tab, setTab] = useSearchParamState('tab', 'pricing', tabCodec) as [string, (v: string) => void];
  const jump = (key: string) => {
    setTab(key);
    document.getElementById(`controls-${key}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  useEffect(() => {
    const el = document.getElementById(`controls-${tab}`);
    if (el && tab !== 'pricing') el.scrollIntoView({ block: 'start' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const Section = ({ id, title, blurb, children }: { id: string; title: string; blurb: string; children: ReactNode }) => (
    <section id={`controls-${id}`} className="scroll-mt-24 rounded-xl border bg-card/60 p-6 space-y-4">
      <div><h3 className="text-lg font-semibold">{title}</h3><p className="text-xs text-muted-foreground mt-0.5">{blurb}</p></div>
      {children}
    </section>
  );
  return (
    <div className="p-8 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold">Business Controls</h2>
          <p className="text-sm text-muted-foreground mt-1">Manage customer-facing pricing, booking rules, complimentary services and promotions from one control center. Every change here records who, when and why.</p>
        </div>
        <span className="rounded-full border px-2.5 py-1 text-xs font-semibold">Admin Control Center</span>
      </div>
      <div className="flex flex-wrap gap-1.5 sticky top-[57px] z-10 py-2 -mx-2 px-2" style={{ background: 'var(--background)' }} role="navigation" aria-label="Business controls sections">
        {TABS.map((t) => <button key={t.key} type="button" onClick={() => jump(t.key)} className={`rounded-full border px-3 py-1 text-xs ${tab === t.key ? 'bg-primary text-primary-foreground border-primary' : 'bg-card'}`}>{t.label}</button>)}
      </div>
      <Section id="pricing" title="Pricing" blurb="Master pricing used by checkout, client portal, quotes and payment workflows. Changes affect future purchases only."><PricingTab /></Section>
      <Section id="rules" title="Booking Rules" blurb="Control when customers may book. Booking availability and the contractual late-booking fee are separate rules."><RulesTab /></Section>
      <Section id="complimentary" title="Complimentary Services" blurb="Gift any normally purchasable service to an existing booking without changing its master price."><ComplimentaryServices /></Section>
      <Section id="coupons" title="Promotions & Coupons" blurb="Create and manage reusable coupon codes. The same workflow L&P uses today."><div className="-mx-8 -mb-8"><Promotions /></div></Section>
      <Section id="notifications" title="Notifications" blurb="Every email and text clients and talent get: turn each on or off, edit the wording, preview it and send yourself a test. Every change is recorded."><NotificationTemplates /></Section>
    </div>
  );
}
